import { AppFormFileUpload } from '@components/AppComponents'
import { isAttachmentTypeBlocked } from '@utils/attachmentAccessControl'
import { DEFAULT_FILE_TYPE } from '@utils/constants'
import { observer } from 'mobx-react'

export enum AttachmentExtensionEnum {
  CLEAN_UP_FILE = 'CLEAN_UP_FILE',
  SUMMARY = 'SUMMARY',
}
export interface UploadComponentProps {
  attachments?: any[]
  attachmentFrom?: string
  attachmentType: string
  objectId: string
  onAdd?: (attachments: any[]) => void
  onChange?: (attachments: any[]) => void
  multiUpload?: boolean
  name?: string
  onRemove?: (ids: string[]) => void
  disabled?: boolean
  label?: string | React.ReactNode
  labelCol?: any
  wrapperCol?: any
  classNameUpload?: string
  type?: string[]
  maxSize?: number
  disabledDelete?: boolean
  // Per-file override for disabledDelete — lock deletion of individual already-saved files
  // while leaving files picked in this session removable.
  isDeleteDisabledForFile?: (file: any) => boolean
  tenantId?: string
  tooltipMessage?: string
  // Hint line under the drag area. Defaults to "(Max {maxSize}MB)" downstream;
  // override it when a field needs to say where the file comes from.
  description?: string
  suffixLabel?: string | React.ReactNode
  /**
   * Rendered in the icon row, ahead of the built-in AI / view / download icons. Pass a function
   * to receive whether the upload box is unusable, so a control placed here follows the field.
   */
  prefixActions?:
    | React.ReactNode
    | ((uploadDisabled: boolean) => React.ReactNode)
  functionCallback?: () => void
  // Fires once every file picked in one go has finished uploading (or failed).
  onUploadsSettled?: () => void
  extensions?: AttachmentExtensionEnum[]
  hideUploadArea?: boolean
  hideButton?: boolean
  hideViewPdfButton?: boolean
  showStaticFileList?: boolean
}

const UploadComponent = ({
  attachments = [],
  hideUploadArea = false,
  showStaticFileList = false,
  hideButton = false,
  hideViewPdfButton = false,
  attachmentFrom = '',
  attachmentType,
  objectId,
  onAdd = attachments => {},
  onChange = attachments => {},
  multiUpload,
  name,
  onRemove = ids => {},
  disabled,
  label,
  labelCol,
  wrapperCol,
  classNameUpload,
  type = null,
  maxSize,
  disabledDelete,
  isDeleteDisabledForFile,
  tenantId,
  tooltipMessage,
  description,
  suffixLabel,
  prefixActions,
  functionCallback,
  onUploadsSettled,
  extensions = [],
}: UploadComponentProps) => {
  const handleAfterUpload = fileList => {
    const allAttachments = multiUpload
      ? attachments || []
      : (attachments || []).filter(i => i.attachmentType !== attachmentType)

    const newAttachments = fileList.map(i => ({
      ...i,
      objectId,
      attachmentType,
      attachmentFrom,
    }))

    onAdd(newAttachments)
    onChange([...allAttachments, ...newAttachments])
    if (functionCallback) {
      functionCallback()
    }
  }

  const handleFileAttachChange = (_, file) => {
    if (file.status === 'removed') {
      handleRemoveFile(file.fileId)
      return
    }

    handleAfterUpload([file])
  }

  const handleRemoveAll = () => {
    onRemove(attachments.map(i => i.fileId))
    onChange([])
  }

  const handleRemoveFile = fileId => {
    const newAttachments = attachments.filter(item => item.fileId !== fileId)
    onRemove([fileId])
    onChange(newAttachments)
  }

  // Tenant Attachment Access Control: when the current user is an offshore staff
  // and this field's attachmentType is in the tenant block list, the field still
  // looks and works exactly as it does for onshore staff — upload, delete and
  // cleanup all stay — but the buttons that reveal the file itself (Summary,
  // View PDF, Download, "Download all") are hidden. Backend enforces the same
  // split; the FE gate is purely UX so the user doesn't hit a 403 mid-flow.
  const isBlockedByOffshorePolicy = isAttachmentTypeBlocked(attachmentType)

  return (
    <AppFormFileUpload
      name={name}
      multiUpload={multiUpload}
      tooltipMessage={tooltipMessage}
      description={description}
      showStaticFileList={showStaticFileList}
      labelCol={labelCol}
      wrapperCol={wrapperCol}
      label={label}
      suffixLabel={suffixLabel}
      prefixActions={prefixActions}
      type={type || DEFAULT_FILE_TYPE}
      fileList={attachments}
      onChange={handleFileAttachChange}
      onRemoveAll={handleRemoveAll}
      onUploadsSettled={onUploadsSettled}
      disabled={disabled}
      classNameUpload={classNameUpload}
      maxSize={maxSize}
      disabledDelete={disabled || disabledDelete}
      // Passed straight through: AppFormFileUpload composes it with disabledDelete above, which
      // already carries the field-level `disabled`.
      isDeleteDisabledForFile={isDeleteDisabledForFile}
      tenantId={tenantId}
      extensions={extensions}
      hideUploadArea={hideUploadArea}
      hideButton={hideButton}
      hideViewPdfButton={hideViewPdfButton}
      contentAccessBlocked={isBlockedByOffshorePolicy}
    />
  )
}

export default observer(UploadComponent)
